import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Button,
  Card,
  DataStatus,
  Dialog,
  Flag,
  IconButton,
  Table,
  TableRow,
  TextInput,
  showNotification,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {memo, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  newVehicleApi,
  useListTrimFeatureAssignmentsQuery,
  type FeatureResponseBody,
} from '@dms/api/new-vehicle';

import {type Nullish} from 'shared/web';

import {PendingChangesBar} from '../../components/PendingChangesBar/PendingChangesBar';
import {PendingChangesBlocker} from '../../components/PendingChangesBlocker/PendingChangesBlocker';
import {AssignmentDialog, type AssignmentValues} from './AssignmentDialog';
import {Cell, column, handleCatalogueError, inclusionFlag, t} from './catalogueUi';
import {FeatureLibraryChoice} from './components/FeatureLibraryChoice';
import {GrossPriceText} from './components/GrossPriceText';
import {SamePriceDialog} from './components/SamePriceDialog';
import {type TrimBatchWrite, type WriteTrims} from './hooks/useTrimBatchSave';
import {optionalPriceError, optionPriceRequired} from './moneyInput';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {PriceWithGross} from './PriceWithGross';
import {updateTrimAssignmentSet} from './updateTrimAssignmentSet';
import {
  isPriceMissing,
  serverCell,
  useEquipmentMatrixDrafts,
  type CellDraft,
  type CellValue,
  type MatrixTrim,
  type WriteTrim,
} from './useEquipmentMatrixDrafts';
import {usePublishedPriceSave} from './usePublishedPriceSave';
import {isInPricelist} from './utils/isInPricelist';
import {trimAssignmentEntry} from './utils/trimAssignmentEntry';
import {trimFeatureChanges} from './utils/trimFeatureChanges';

/** `name` is the Variant name the History uses (variantChangeName). */
type Variant = {id: string; orderCode: string; trimId: string; name: string};
type MatrixProps = {
  modelYearId: string;
  trims: MatrixTrim[];
  features: FeatureResponseBody[];
  variants: Variant[];
  currency: string;
  vatRate: string | Nullish;
  canWrite: boolean;
  canViewCost: boolean;
};
type EditedCell = {trim: MatrixTrim; feature: FeatureResponseBody};

const NEXT_VALUE: Record<CellValue, CellValue> = {
  NOT_OFFERED: 'STANDARD',
  STANDARD: 'OPTIONAL',
  OPTIONAL: 'NOT_OFFERED',
};
const valueFlag = (value: CellValue) => inclusionFlag(value === 'NOT_OFFERED' ? null : value);
// Stable identity: the blocker re-registers its router predicate when this changes.
const shouldBlockNavigation = () => true;

const cellName = (feature: string, trim: string, value: CellValue, isUnsaved: boolean) => {
  const options = {feature, trim, inclusion: valueFlag(value).label};
  return isUnsaved
    ? t(
        'entity.newVehicle.catalogue.inclusionCellNameUnsaved',
        '{feature} on {trim}: {inclusion}, unsaved',
        options
      )
    : t(
        'entity.newVehicle.catalogue.inclusionCellName',
        '{feature} on {trim}: {inclusion}',
        options
      );
};

// Platform foundation has no visually hidden element for a live region.
const StatusRegion = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

// A native button: the shared `Clickable` has no disabled a11y contract, and the cell needs a
// focusable, labelled control that Enter and Space activate.
const InclusionButton = styled.button<{$isUnsaved: boolean}>`
  position: relative;
  display: inline-flex;
  padding: 0;
  border: 0;
  border-radius: ${({theme}) => theme.radii.small};
  background: none;
  cursor: pointer;
  user-select: none;
  transition-property: scale, opacity;
  transition-duration: 150ms;
  transition-timing-function: ease-out;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:active:not(:disabled) {
    scale: 0.96;
  }

  &:focus-visible {
    outline: var(--outline-default) ${({theme}) => theme.colors.general.accent};
    outline-offset: var(--outline-offset);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  /* An unsaved value carries a dot in the pending bar's warning colour. */
  &::after {
    content: '';
    display: ${({$isUnsaved}) => ($isUnsaved ? 'block' : 'none')};
    position: absolute;
    top: -${({theme}) => theme.getSize(1)};
    right: -${({theme}) => theme.getSize(1)};
    width: ${({theme}) => theme.getSize(2)};
    height: ${({theme}) => theme.getSize(2)};
    border-radius: ${({theme}) => theme.radii.circular};
    background-color: ${({theme}) => theme.colors.severity.warning};
  }
`;

/**
 * One equipment × trim cell. A click cycles a draft value (Not offered, Standard, Optional); drafts
 * are saved for the whole matrix at once. Cells of the same trim share one cached assignment query,
 * and the API only replaces a trim's set as a whole, so every save PUTs that trim's complete list.
 */
const InclusionCell = memo(function InclusionCell(
  props: Pick<MatrixProps, 'currency' | 'vatRate' | 'canWrite'> & {
    isPublished: boolean;
    trim: MatrixTrim;
    feature: FeatureResponseBody;
    draft: CellDraft | Nullish;
    startPrice: string | Nullish;
    isSaving: boolean;
    onDraft: (
      trim: MatrixTrim,
      feature: FeatureResponseBody,
      draft: CellDraft,
      server: CellDraft
    ) => void;
    onEditDetails: (cell: EditedCell) => void;
  }
) {
  const query = useListTrimFeatureAssignmentsQuery({trimId: props.trim.id});
  const assignments = query.currentData;
  const assignment = assignments?.find(({featureId}) => featureId === props.feature.id);
  const server = serverCell(assignment);
  const cell = props.draft ?? server;
  const value = cell.value;
  const isUnsaved = isNotNil(props.draft);
  const canEditPrice =
    props.canWrite && !(props.isPublished && server.value === 'OPTIONAL' && !isUnsaved);
  const isConditional = Boolean(
    assignment &&
      (assignment.appliesIf.anyOfVariantIds.length || assignment.appliesIf.allOfFeatureIds.length)
  );

  const cycle = () => {
    const next = NEXT_VALUE[value];
    const optionalStart = server.value === 'OPTIONAL' ? server.priceNet : (props.startPrice ?? '');
    props.onDraft(
      props.trim,
      props.feature,
      {value: next, priceNet: next === 'OPTIONAL' ? optionalStart : ''},
      server
    );
  };

  return (
    <Cell>
      <VStack spacing={1} align="flex-start">
        {props.canWrite ? (
          <HStack spacing={1} align="center">
            <InclusionButton
              type="button"
              data-testid={`inclusion-${props.trim.id}-${props.feature.id}`}
              aria-label={cellName(props.feature.name, props.trim.name, value, isUnsaved)}
              aria-busy={query.isLoading}
              disabled={isNil(assignments)}
              $isUnsaved={isUnsaved}
              onClick={cycle}
            >
              <Flag {...valueFlag(value)} />
            </InclusionButton>
            <Show when={isNotNil(assignment)}>
              <IconButton
                icon="editor/mode"
                size="small"
                aria-label={t(
                  'entity.newVehicle.catalogue.priceAndConditionsOnTrim',
                  'Price and conditions of {feature} on {trim}',
                  {feature: props.feature.name, trim: props.trim.name}
                )}
                aria-haspopup="dialog"
                isDisabled={isUnsaved || props.isSaving}
                onClick={() => props.onEditDetails({trim: props.trim, feature: props.feature})}
              />
            </Show>
          </HStack>
        ) : (
          <Text size="small" color={assignment ? undefined : 'tertiary'}>
            {valueFlag(server.value).label}
          </Text>
        )}
        {match({value, canEditPrice})
          .with({value: 'STANDARD'}, () => (
            <Text size="xSmall" color="secondary">
              {t('entity.newVehicle.authoring.included', 'Included')}
            </Text>
          ))
          .with({value: 'OPTIONAL', canEditPrice: true}, () => (
            <VStack spacing={1}>
              <TextInput
                aria-label={t(
                  'entity.newVehicle.catalogue.priceOnTrimNet',
                  'Price of {feature} on {trim}, net',
                  {feature: props.feature.name, trim: props.trim.name}
                )}
                value={cell.priceNet}
                suffix={props.currency}
                isInvalid={isPriceMissing(cell)}
                errorMessage={
                  isPriceMissing(cell)
                    ? (optionalPriceError(cell.priceNet) ?? optionPriceRequired())
                    : undefined
                }
                isDisabled={isNil(assignments)}
                onChange={(priceNet) =>
                  props.onDraft(
                    props.trim,
                    props.feature,
                    {value: 'OPTIONAL', priceNet: priceNet ?? ''},
                    server
                  )
                }
              />
              <GrossPriceText
                net={cell.priceNet}
                currency={props.currency}
                vatRate={props.vatRate}
              />
            </VStack>
          ))
          .with({value: 'OPTIONAL'}, () => (
            <PriceWithGross
              amount={cell.priceNet}
              currency={props.currency}
              vatRate={props.vatRate}
              size="xSmall"
              align="left"
            />
          ))
          .otherwise(() => null)}
        <Show when={isConditional}>
          <Text size="xSmall" color="secondary">
            {t('entity.newVehicle.catalogue.conditional', 'Conditional')}
          </Text>
        </Show>
      </VStack>
    </Cell>
  );
});

/**
 * Every equipment item of the model year against every trim, so what each trim includes is visible
 * at once. An item added from the library stays a row until Discard, or until a save sets it on a
 * trim, which puts it on the year's list. One that was archived in the library meanwhile is dropped
 * when a save starts, because no trim can take it any more.
 */
export function TrimEquipmentMatrix(props: MatrixProps) {
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const variantOf = (variantId: string) => props.variants.find(({id}) => id === variantId);
  // A published year asks for a new pricelist version when the trim's write changes a priced item.
  // A Draft trim (as read fresh for the write) is outside the price list: its write has no rows.
  const trimChanges = (write: TrimBatchWrite) =>
    !isInPricelist.trim(write.trim.publicationStatus)
      ? []
      : trimFeatureChanges({
          trimId: write.trim.id,
          trimName: write.trim.name,
          stored: write.stored,
          next: write.next,
          // The rows added from the library too, so the dialog names them.
          features: matrix.features,
          variantName: (variantId) => variantOf(variantId)?.name ?? variantId,
          variantOrderCode: (variantId) => variantOf(variantId)?.orderCode ?? variantId,
        });
  // One trim's assignment write as a batch entry; it shares the key of the trim's other writes. In a
  // group, its build reads the trim and its assignments again at Confirm.
  const trimEntry = (write: TrimBatchWrite) =>
    trimAssignmentEntry({
      trim: write.trim,
      title: write.trim.name,
      items: trimChanges(write),
      stored: write.stored,
      next: write.next,
      readTrimVersion: () =>
        getTrim({trimId: write.trim.id}, false)
          .unwrap()
          .then((trim) => trim.version),
      readAssignments: () => getAssignments({trimId: write.trim.id}, false).unwrap(),
    });
  const writeTrim: WriteTrim = (write) => {
    const {items, ...entry} = trimEntry(write);
    return publishedPrice.writePricedItemChange({items, entry, write: write.put});
  };
  const writeTrims: WriteTrims = (writes, onTrimFailed) =>
    publishedPrice.writePricedItemChanges({
      entries: writes.map(trimEntry),
      onEntryFailed: (entryIndex) => {
        const failed = writes[entryIndex];
        if (!isNil(failed)) {
          onTrimFailed(failed.trim.id);
        }
      },
    });
  const matrix = useEquipmentMatrixDrafts({
    modelYearId: props.modelYearId,
    trims: props.trims,
    features: props.features,
    writeTrim,
    writeTrims,
    readIsPublished: () => publishedPrice.readIsPublished(),
  });
  const failedTrim = props.trims.find(({id}) => id === matrix.failedTrimId);
  const {features} = matrix;
  const [filling, setFilling] = useState<FeatureResponseBody | null>(null);
  // A published year sets the kept prices of online trims by one price adjustment of the item, and
  // fills the cells that are drafts anyway (new, newly Optional, on a Draft trim) once it is created,
  // so a cancelled version leaves the row as it was. When no kept price changes (none, or all already
  // at the price), no adjustment is needed and they are filled at once.
  const fillRow = (feature: FeatureResponseBody, priceNet: string) =>
    publishedPrice
      .readIsPublished()
      .then(async (isPublished) => {
        const fillDrafts = () => matrix.fillRow(feature, priceNet, isPublished);
        if (isPublished && matrix.changesKeptPrice(feature.id, priceNet)) {
          await publishedPrice.confirmAdjustment(
            {kind: 'set', value: priceNet, scope: 'feature', resourceIds: [feature.id]},
            {onDone: fillDrafts}
          );
        } else {
          fillDrafts();
        }
        setFilling(null);
      })
      .catch(handleCatalogueError);
  // One details dialog for the whole matrix: a cell gets no arrays, so memo holds when the page
  // re-renders on every refetch a save causes.
  const [edited, setEdited] = useState<EditedCell | null>(null);
  const featureIds = features.map(({id}) => id);
  const startPrices = Object.fromEntries(features.map(({id}) => [id, matrix.startPrice(id)]));
  const editedAssignment = useListTrimFeatureAssignmentsQuery(
    edited ? {trimId: edited.trim.id} : skipToken
  ).currentData?.find(({featureId}) => featureId === edited?.feature.id);
  // The dialog closes when its trim, equipment item or assignment leaves the matrix meanwhile.
  // It reads the trim as listed now, so a trim published meanwhile (by another author) counts.
  const editedTrim = props.trims.find(({id}) => id === edited?.trim.id);
  const openDialog =
    edited && editedAssignment && editedTrim && featureIds.includes(edited.feature.id)
      ? {edited, trim: editedTrim, assignment: editedAssignment}
      : null;
  // Forget a closed dialog's cell, so it cannot reopen by itself when that cell comes back.
  if (edited && !openDialog) {
    setEdited(null);
  }

  const saveDetails = (cell: EditedCell, values: AssignmentValues) =>
    matrix
      .replaceAssignments(
        cell.trim.id,
        (freshAssignments) =>
          updateTrimAssignmentSet(freshAssignments, cell.feature.id, values, props.canViewCost),
        writeTrim
      )
      .then(({outcome}) => {
        // A write with a new pricelist version showed its own notification; a cancelled one none.
        if (outcome === 'written') {
          showNotification.success(
            t('entity.newVehicle.catalogue.assignmentsSaved', 'Trim assignments saved')
          );
        }
        return outcome;
      });

  return (
    <Card
      title={t('entity.newVehicle.catalogue.equipmentByTrim', 'Equipment by trim')}
      subtitle={t(
        'entity.newVehicle.catalogue.equipmentByTrimPriceHint',
        'Standard is included in the trim price, Optional is sold at its net price on that trim (0 makes it a free choice). Click a status to change it.'
      )}
    >
      <Show when={props.canWrite && props.trims.length > 0}>
        <Box paddingBottom={4}>
          <FeatureLibraryChoice
            yearFeatures={[]}
            excludedIds={featureIds}
            label={t('entity.newVehicle.catalogue.addFromLibrary', 'Add from library')}
            placeholder={t('entity.newVehicle.catalogue.addFromLibrary', 'Add from library')}
            onPick={matrix.pin}
          />
        </Box>
      </Show>
      <Show when={failedTrim}>
        {(trim) => (
          <Box paddingBottom={4}>
            <Alert
              variant="error"
              message={t(
                'entity.newVehicle.catalogue.trimAssignmentsNotSaved',
                'The equipment of {trim} could not be saved. No trim of this save was saved.',
                {trim: trim.name}
              )}
            />
          </Box>
        )}
      </Show>
      <DataStatus
        isEmpty={!props.trims.length || !features.length}
        emptyMessage={t(
          'entity.newVehicle.catalogue.equipmentByTrimEmpty',
          'Add trim levels and equipment first'
        )}
      >
        <Table
          columns={[
            column(t('entity.newVehicle.catalogue.equipment', 'Equipment'), {width: '24%'}),
            ...props.trims.map(({name}) => column(name)),
          ]}
        >
          {features.map((feature) => (
            <TableRow key={feature.id}>
              <Cell>
                <VStack spacing={0}>
                  <Text size="small" alternative>
                    {feature.name}
                  </Text>
                  <Show when={!feature.standaloneOrderable}>
                    <Text size="xSmall" color="secondary">
                      {t('entity.newVehicle.rules.packageOnly', 'Package-only')}
                    </Text>
                  </Show>
                  <Show when={props.canWrite && matrix.hasOptionalCell(feature.id)}>
                    <Box>
                      <Button
                        variant="link"
                        size="small"
                        title={t(
                          'entity.newVehicle.catalogue.samePriceOnAllTrims',
                          'Same price on all trims'
                        )}
                        isDisabled={matrix.isSaving}
                        onClick={() => setFilling(feature)}
                      />
                    </Box>
                  </Show>
                </VStack>
              </Cell>
              {props.trims.map((trim) => (
                <InclusionCell
                  key={trim.id}
                  trim={trim}
                  feature={feature}
                  currency={props.currency}
                  vatRate={props.vatRate}
                  canWrite={props.canWrite}
                  isPublished={publishedPrice.isPublished}
                  isSaving={matrix.isSaving}
                  draft={matrix.drafts[trim.id]?.[feature.id]}
                  startPrice={startPrices[feature.id]}
                  onDraft={matrix.setDraft}
                  onEditDetails={setEdited}
                />
              ))}
            </TableRow>
          ))}
        </Table>
      </DataStatus>
      <Dialog
        isOpen={isNotNil(openDialog)}
        onClose={() => setEdited(null)}
        withAdditionalFooter
        title={
          openDialog
            ? t('entity.newVehicle.catalogue.assignmentOnTrim', '{feature} on {trim}', {
                feature: openDialog.edited.feature.name,
                trim: openDialog.edited.trim.name,
              })
            : ''
        }
      >
        <Show when={openDialog}>
          {(dialog) => (
            <AssignmentDialog
              modelYearId={props.modelYearId}
              assignment={dialog.assignment}
              yearFeatures={props.features}
              variants={props.variants.filter(({trimId}) => trimId === dialog.edited.trim.id)}
              isTrimOutsidePricelist={!isInPricelist.trim(dialog.trim.publicationStatus)}
              canViewCost={props.canViewCost}
              vatRate={props.vatRate}
              save={(values) => saveDetails(dialog.edited, values)}
              // A save that finishes after its dialog was closed must not close another cell's.
              onDone={() => setEdited((current) => (current === dialog.edited ? null : current))}
            />
          )}
        </Show>
      </Dialog>
      <SamePriceDialog
        itemName={filling?.name}
        hint={t(
          'entity.newVehicle.catalogue.samePriceOnAllTrimsHint',
          'Sets this price on every trim where the item is Optional. Trims where it is free (0) keep 0; change those on their own.'
        )}
        startPrice={filling ? matrix.startPrice(filling.id) : undefined}
        currency={props.currency}
        vatRate={props.vatRate}
        onApply={(priceNet) => (filling ? fillRow(filling, priceNet) : Promise.resolve())}
        onClose={() => setFilling(null)}
      />
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      {/* Announces a cell's new value; the cell's own label change is not announced. */}
      <StatusRegion role="status">
        {matrix.lastChange
          ? cellName(
              matrix.lastChange.feature.name,
              matrix.lastChange.trim.name,
              matrix.lastChange.value,
              matrix.lastChange.isUnsaved
            )
          : ''}
      </StatusRegion>
      <Show when={matrix.pendingCount > 0}>
        <PendingChangesBar
          title={t(
            'entity.newVehicle.catalogue.unsavedEquipmentChanges',
            '{count} unsaved equipment changes',
            {count: matrix.pendingCount}
          )}
          isSaving={matrix.isSaving}
          isSaveDisabled={matrix.draftCount === 0}
          onDiscard={matrix.discard}
          onSave={() => void matrix.save()}
        />
      </Show>
      <PendingChangesBlocker
        isDirty={matrix.pendingCount > 0}
        isSaving={matrix.isSaving}
        shouldBlockNavigation={shouldBlockNavigation}
        onSave={matrix.save}
      />
    </Card>
  );
}
